import { useCallback, useEffect, useState } from 'react'
import { Phone, Plus, X, Trash2, Clock, Calendar, MessageSquare, Copy, Check, Send, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface Shift {
  id: number
  rotation: string
  person_name: string
  person_contact: string
  starts_at: string
  ends_at: string
  notes: string
}

export default function OnCallPage() {
  const [shifts, setShifts] = useState<Shift[]>([])
  const [now, setNow] = useState<Shift[]>([])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    rotation: 'platform', person_name: '', person_contact: '',
    starts_at: '', ends_at: '', notes: '',
  })

  const fetchAll = useCallback(async () => {
    try {
      const [a, b] = await Promise.allSettled([
        api.get('/api/oncall', { params: { days_ahead: 90 } }),
        api.get('/api/oncall/now'),
      ])
      if (a.status === 'fulfilled') setShifts(a.value.data?.shifts || [])
      if (b.status === 'fulfilled') setNow(b.value.data?.current || [])
    } catch {}
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.person_name || !form.starts_at || !form.ends_at) return
    try {
      await api.post('/api/oncall', {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      })
      setShowForm(false)
      setForm({ rotation: 'platform', person_name: '', person_contact: '', starts_at: '', ends_at: '', notes: '' })
      fetchAll()
    } catch {}
  }

  const remove = async (id: number) => {
    if (!confirm('Delete shift?')) return
    await api.delete(`/api/oncall/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Phone size={24} className="text-blue" /> On-call Rotation
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Who's covering what, right now and the next 90 days. Auto-feeds the Active Situation Room.
          </p>
        </div>
        <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs">
          <Plus size={12} /> Add shift
        </button>
      </div>

      {now.length > 0 && (
        <div className="card p-4 border border-emerald/30 bg-emerald/[0.04]">
          <h2 className="text-sm font-semibold text-emerald-light mb-2 flex items-center gap-2">
            <Clock size={14} /> On call right now
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {now.map(s => (
              <div key={s.id} className="rounded-md border border-emerald/40 bg-emerald/5 p-2">
                <div className="text-[9px] uppercase tracking-wider text-emerald-light font-semibold">{s.rotation}</div>
                <div className="text-text-primary font-semibold text-sm mt-0.5">{s.person_name}</div>
                {s.person_contact && <div className="text-[10px] text-text-muted font-mono">{s.person_contact}</div>}
                <div className="text-[9px] text-text-muted mt-1">until {new Date(s.ends_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Add shift</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.rotation} onChange={e => setForm({...form, rotation: e.target.value})} placeholder="Rotation (platform / security / data)" className="input py-1.5" />
            <input value={form.person_name} onChange={e => setForm({...form, person_name: e.target.value})} placeholder="Person name" className="input py-1.5" />
            <input value={form.person_contact} onChange={e => setForm({...form, person_contact: e.target.value})} placeholder="Contact (email / slack / phone)" className="input py-1.5 md:col-span-2" />
            <label className="space-y-0.5"><span className="text-text-muted">Starts at</span>
              <input type="datetime-local" value={form.starts_at} onChange={e => setForm({...form, starts_at: e.target.value})} className="input w-full py-1.5" /></label>
            <label className="space-y-0.5"><span className="text-text-muted">Ends at</span>
              <input type="datetime-local" value={form.ends_at} onChange={e => setForm({...form, ends_at: e.target.value})} className="input w-full py-1.5" /></label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <IntegrationsPanel />

      <div className="card p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
              <th className="text-left py-2 px-2">Rotation</th>
              <th className="text-left py-2 px-2">Person</th>
              <th className="text-left py-2 px-2">Window</th>
              <th className="text-right py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {shifts.length === 0 && (
              <tr><td colSpan={4} className="text-center py-6 text-text-muted text-[11px] italic">
                No upcoming shifts. Add one above.
              </td></tr>
            )}
            {shifts.map(s => (
              <tr key={s.id} className="border-b border-border/50">
                <td className="py-1.5 px-2 font-mono uppercase text-[10px] text-blue-light">{s.rotation}</td>
                <td className="py-1.5 px-2">
                  <div className="text-text-primary font-medium">{s.person_name}</div>
                  <div className="text-[9px] text-text-muted">{s.person_contact}</div>
                </td>
                <td className="py-1.5 px-2 text-[10px] text-text-muted">
                  {new Date(s.starts_at).toLocaleString()}<br />
                  → {new Date(s.ends_at).toLocaleString()}
                </td>
                <td className="py-1.5 px-2 text-right">
                  <button type="button" onClick={() => remove(s.id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Integrations panel: iCal feed + Outlook calendar push + Teams notify ─────
function IntegrationsPanel() {
  const [status, setStatus] = useState<any>(null)
  const [copied, setCopied] = useState(false)
  const [pushing, setPushing] = useState(false)
  const [testing, setTesting] = useState(false)
  const [pushResult, setPushResult] = useState<any>(null)
  const [testResult, setTestResult] = useState<any>(null)

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/api/oncall/integrations/status')
      setStatus(r.data)
    } catch {}
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const icalUrl = status?.ical_feed_url
    ? `${window.location.origin}${status.ical_feed_url}`
    : ''

  const copyIcal = async () => {
    if (!icalUrl) return
    await navigator.clipboard.writeText(icalUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const pushCalendar = async () => {
    setPushing(true); setPushResult(null)
    try {
      const r = await api.post('/api/oncall/integrations/push-calendar', null, { params: { only_future: true } })
      setPushResult(r.data)
    } catch (e: any) {
      setPushResult({ ok: false, error: e?.response?.data?.detail || 'failed' })
    } finally { setPushing(false) }
  }

  const teamsTest = async () => {
    setTesting(true); setTestResult(null)
    try {
      const r = await api.post('/api/oncall/integrations/teams-test', { message: 'On-call integration smoke test' })
      setTestResult(r.data)
    } catch (e: any) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || 'failed' })
    } finally { setTesting(false) }
  }

  return (
    <div className="card p-4 border border-border">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <MessageSquare size={14} className="text-blue-light" /> Outlook + Teams integration
        </h2>
        <button type="button" onClick={refresh} className="text-text-muted hover:text-text-primary">
          <RefreshCw size={11} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* iCal — always available */}
        <div className="rounded p-3 bg-surface-raised border border-border">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Calendar size={11} className="text-emerald" />
            <span className="text-[11px] font-semibold text-text-primary">iCal feed</span>
            <span className="badge text-[8px] bg-emerald/15 text-emerald border border-emerald/30 ml-auto">always on</span>
          </div>
          <p className="text-[10px] text-text-secondary mb-2">
            Subscribe in Outlook (Add Calendar → From Internet) or Teams (channel → Calendar tab).
          </p>
          <div className="flex items-center gap-1">
            <input readOnly value={icalUrl} className="input py-1 text-[10px] font-mono flex-1" />
            <button type="button" onClick={copyIcal} className="btn-ghost text-[10px] px-2" title="Copy">
              {copied ? <Check size={10} className="text-emerald" /> : <Copy size={10} />}
            </button>
          </div>
        </div>

        {/* Outlook calendar push (Graph) */}
        <div className="rounded p-3 bg-surface-raised border border-border">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Calendar size={11} className="text-blue-light" />
            <span className="text-[11px] font-semibold text-text-primary">Push to Outlook</span>
            <span className={clsx('badge text-[8px] ml-auto',
              status?.entra_configured
                ? 'bg-emerald/15 text-emerald border border-emerald/30'
                : 'bg-amber/15 text-amber-light border border-amber/30')}>
              {status?.entra_configured ? 'Entra ready' : 'set Entra env'}
            </span>
          </div>
          <p className="text-[10px] text-text-secondary mb-1">
            Target: <code className="font-mono text-text-primary">{status?.calendar_target || '—'}</code>
          </p>
          <button type="button" onClick={pushCalendar} disabled={pushing || !status?.entra_configured}
            className="btn-primary text-[10px] py-1 w-full">
            {pushing ? <RefreshCw size={10} className="animate-spin" /> : <Send size={10} />}
            Sync future shifts
          </button>
          {pushResult && (
            <div className={clsx('mt-2 text-[10px]', pushResult.ok ? 'text-emerald' : 'text-rose-light')}>
              {pushResult.ok
                ? <>✓ pushed {pushResult.pushed} of {pushResult.total_shifts} ({pushResult.failed?.length || 0} failed)</>
                : <>✗ {pushResult.error}</>}
            </div>
          )}
        </div>

        {/* Teams notify */}
        <div className="rounded p-3 bg-surface-raised border border-border">
          <div className="flex items-center gap-1.5 mb-1.5">
            <MessageSquare size={11} className="text-blue-light" />
            <span className="text-[11px] font-semibold text-text-primary">Teams channel</span>
            <span className={clsx('badge text-[8px] ml-auto',
              (status?.teams_webhook_set || status?.teams_graph_target)
                ? 'bg-emerald/15 text-emerald border border-emerald/30'
                : 'bg-amber/15 text-amber-light border border-amber/30')}>
              {status?.teams_webhook_set ? 'webhook' : status?.teams_graph_target ? 'Graph' : 'unset'}
            </span>
          </div>
          <p className="text-[10px] text-text-secondary mb-1">
            Posts adaptive cards on rotation change + SEV1.
          </p>
          <button type="button" onClick={teamsTest} disabled={testing || !(status?.teams_webhook_set || status?.teams_graph_target)}
            className="btn-primary text-[10px] py-1 w-full">
            {testing ? <RefreshCw size={10} className="animate-spin" /> : <Send size={10} />}
            Send test card
          </button>
          {testResult && (
            <div className={clsx('mt-2 text-[10px]', testResult.ok ? 'text-emerald' : 'text-rose-light')}>
              {testResult.ok
                ? <>✓ posted via {testResult.via}</>
                : <>✗ {testResult.error}</>}
            </div>
          )}
        </div>
      </div>

      {!status?.entra_configured && (
        <div className="mt-3 p-2 bg-amber/5 border border-amber/30 rounded text-[10px] text-amber-light">
          <div className="flex items-start gap-1">
            <AlertTriangle size={10} className="mt-0.5 shrink-0" />
            <div>
              Entra not configured. Set in <code className="font-mono">backend/.env</code>:
              <code className="block font-mono mt-1 text-text-secondary">
                ENTRA_TENANT_ID=… · ENTRA_CLIENT_ID=… · ENTRA_CLIENT_SECRET=… · ENTRA_ONCALL_CALENDAR_USER=upn@domain
              </code>
              Grant app permissions: <code className="font-mono">Calendars.ReadWrite</code>, <code className="font-mono">User.Read.All</code>.
              For Teams: easiest path is an Incoming Webhook (set <code className="font-mono">TEAMS_ONCALL_WEBHOOK_URL</code>).
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
